import { useEffect, useState } from 'react';
import { getCollectionsByNetwork } from '@/lib/collections';
import { fetchSandboxCollectionTokens } from '@/lib/tzkt';
import { fetchFilteredTokens, fetchCollectionTraits } from '@/lib/api';
import { getCardImageUrl } from '@/lib/ipfs';
import { useAppNetwork } from '@/hooks/useAppNetwork';
import { CollectionCard, CollectionWithStats } from '@/components/ui/CollectionCard';
import { CollectionStats } from '@/lib/types';

export default function CollectionsPage() {
  const [collections, setCollections] = useState<CollectionWithStats[]>([]);
  const { network: appNetwork } = useAppNetwork();

  useEffect(() => {
    const isSandbox = appNetwork === 'sandbox';

    const networkCollections = getCollectionsByNetwork(appNetwork);

    setCollections(
      networkCollections.map(c => ({ ...c, isLoading: true }))
    );

    networkCollections.forEach(async (collection) => {
      try {
        let stats: CollectionStats | undefined;
        let sampleImage: string | undefined;

        if (isSandbox) {
          const result = await fetchSandboxCollectionTokens(collection.address, { offset: 0, limit: 1 });
          if (result.items.length > 0 && result.items[0].metadata?.image) {
            sampleImage = getCardImageUrl(result.items[0].metadata.image);
          }
          stats = {
            address: collection.address,
            tokenCount: result.pagination.totalCount || result.items.length,
            holderCount: 1,
            lastUpdated: new Date(),
          };
        } else {
          // Use our API server for mainnet collections (has cached data)
          const traitsPromise = fetchCollectionTraits(collection.address)
            .then(data => ({
              address: collection.address,
              tokenCount: data.totalSupply,
              holderCount: 0, // Not available from traits endpoint
              lastUpdated: new Date(),
            }))
            .catch(() => undefined);
          const samplePromise = fetchFilteredTokens(collection.address, { limit: 1 })
            .then(result => {
              if (result.items.length > 0 && result.items[0].metadata?.image) {
                return getCardImageUrl(result.items[0].metadata.image);
              }
              return undefined;
            })
            .catch(() => undefined);

          [stats, sampleImage] = await Promise.all([traitsPromise, samplePromise]);
        }

        setCollections(prev =>
          prev.map(c =>
            c.slug === collection.slug
              ? { ...c, stats, sampleImage, isLoading: false }
              : c
          )
        );
      } catch (err) {
        console.error('Failed to fetch collection data:', collection.slug, err);
        setCollections(prev =>
          prev.map(c =>
            c.slug === collection.slug
              ? { ...c, isLoading: false }
              : c
          )
        );
      }
    });
  }, [appNetwork]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Collections</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {collections.map((collection) => (
            <CollectionCard key={collection.slug} collection={collection} />
          ))}
        </div>

        {collections.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading collections...
          </div>
        )}
      </div>
    </div>
  );
}
